import React, { FC, useCallback, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../common/toast/hooks/useToast'
import { Page, PageFormProps } from './Page.d'
import { Card } from 'primereact/card'
import { api } from '../../../../auth'
import Common from '../../../common/Common'
import { Button, Alert, Row, Col } from 'react-bootstrap'
import { useAppSelector } from '../../../../store/hooks'
import PageHeader from '../../../layout/page/PageHeader'
import { Language } from '../../../../features/security/security.slice'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import TextField from '../../../common/forms/fields/TextField'
import ImageField from '../../../common/forms/fields/ImageField'
import DeleteDialog from '../../../common/dialogs/DeleteDialog'
import TextAreaField from '../../../common/forms/fields/TextAreaField'
import slugify from 'react-slugify'
import CallToActions from '../../../common/forms/fields/CallToActions'
import { handleAxiosErrors } from '../../../common/forms/errors/Helper'
import RenderFieldsErrors from '../../../common/forms/errors/RenderFieldsErrors'
import RenderTranslatedFields from '../../../common/forms/fields/translated-fields/RenderTranslatedFields'
import SelectField from '../../../common/forms/fields/SelectField'
import FormActions from '../../../common/forms/FormActions'
import { getGroupTranslation } from '../../../common/utility/helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import RenderFormikErrors from '../../../common/forms/errors/RenderFormikErrors'

const PageForm: FC<PageFormProps> = ({
    id = null,
    inPopup = true,
    config = {},
    onClose,
}) => {
    const isViewOrEdit = id !== null
    const pageCode = isViewOrEdit ? 'PAGE_' + id : 'TMPSET'
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)

    const navigate = useNavigate()
    const axiosPrivate = api

    const emptyPage: Page = {
        page_title: '',
        code: '',
        name: '',
        header_size_id: null,
        meta_description: '',
        meta_keywords: '',
        main_header_top: '',
        main_header_middle_big: '',
        main_header_bottom: '',
        header_image_id: null,
        header_image: '',
        remove_header_image: false,
        is_template: false,
        ctas: {},
    }

    const [pageDialog, setPageDialog] = useState(true)
    const [page, setPage] = useState<Page>(emptyPage)
    const [availablePages, setAvailablePages] = useState<[]>([])
    const [headerSizes, setHeaderSizes] = useState<[]>([])
    const [deletedCtas, setDeletedCtas] = useState<[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [allowRemoveImage, setAllowRemoveImage] = useState(false)
    const [previewSrc, setPreviewSrc] = useState('')
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})
    const [translations, setTranslations] = useState<{}>({})

    let schema = {
        code: Yup.string().trim().required('Enter Page Url'),
        name: Yup.string().trim().required('Enter Page Internal Name'),
    }

    let _initialValues = {
        ...page,
        value: pageCode + '_VALUE',
        main_header_top: pageCode + '_MAIN_HEADER_TOP',
        main_header_middle_big: pageCode + '_MAIN_HEADER_MIDDLE_BIG',
        main_header_bottom: pageCode + '_MAIN_HEADER_BOTTOM',
    };

    (Object.values(initData.languages.data) as Language[]).map((lang) => {
        // schema[`main_header_middle_big_${lang.id}`] = Yup.string()
        //     .trim()
        //     .required(
        //         `The Main Header Middle (Big) (${lang.name}) field is required`
        //     )
        // _initialValues[`main_header_middle_big_${lang.id}`] = ''
        return true
    })

    const [initialValues, setInitialValues] = useState(_initialValues)

    const PageFormSchema = Yup.object().shape(schema)

    const { triggerToast } = useToast()

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        const routePath = isViewOrEdit
            ? '/page-components/pages/' + id
            : '/page-components/pages/new'

        try {
            const response = await axiosPrivate.get(routePath).finally(() => {
                setIsLoading(false)
            })
            if (response.data?.success === true) {
                if (response.data.data.page) {
                    const pageData = response.data.data.page
                    const pageHeaders = response.data.data.page_headers || {}
                    const translations = response.data.data.translations || {}
                    setPage(pageData)
                    const translationCodes = [
                        { key: 'main_header_top', code: pageHeaders.main_header_top },
                        { key: 'main_header_middle_big', code: pageHeaders.main_header_middle_big },
                        { key: 'main_header_bottom', code: pageHeaders.main_header_bottom },
                    ]
                    const nextInitial: Record<string, unknown> = {
                        ...initialValues,
                        ...pageData,
                    }
                    Object.keys(initData.languages.data).forEach((key) => {
                        const lang: Language = initData.languages.data[key] as Language
                        const locale = lang?.locale ?? key
                        const groupTranslations: Record<string, any> = getGroupTranslation(
                            translations,
                            translationCodes,
                            locale
                        )
                        nextInitial[`main_header_top_${lang.id}`] = groupTranslations['main_header_top']
                        nextInitial[`main_header_middle_big_${lang.id}`] = groupTranslations['main_header_middle_big']
                        nextInitial[`main_header_bottom_${lang.id}`] = groupTranslations['main_header_bottom']
                    })
                    setInitialValues(nextInitial as typeof initialValues)
                }

                setAvailablePages(response.data.data.available_pages || [])
                setHeaderSizes(response.data.data.header_sizes || [])
                setTranslations(response.data.data.translations)
                if (isViewOrEdit) {
                    setAllowRemoveImage(
                        response.data.data.page.header_image_id !== null
                    )
                    setPreviewSrc(
                        response.data.data.page.header_image_id
                            ? `${process.env.REACT_APP_API_ROOT_URL}/media/download/${response.data.data.page.header_image_id}`
                            : ''
                    )
                } else {
                    setPreviewSrc('')
                }
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            // TODO: Sort out error handling!
            if (!err?.response) {
                setErrMsg('No Server Response')
            } else if (err.response?.status === 404) {
                setErrMsg('Record was not found')
            } else {
                setErrMsg('Failed to load page')
            }
        }
    }, [axiosPrivate, id, initData.languages.data, isViewOrEdit])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    const saveRecord = async (record: any) => {
        setErrMsg('')
        setIsSubmitting(true)

        record.remove_header_image =
            !record.header_image &&
            record.header_image_id !== null &&
            !allowRemoveImage

        try {
            const routePath = isViewOrEdit
                ? '/page-components/pages/' + id
                : '/page-components/pages/create'
            const response = await axiosPrivate
                .post(
                    routePath,
                    { ...record, deletedCtas },
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                )
                .finally(() => {
                    setIsSubmitting(false)
                })

            if (response.data?.success === true) {
                triggerToast({
                    severity: 'success',
                    summary: config?.submitSuccessMsg?.header || 'Successful',
                    detail:
                        config?.submitSuccessMsg?.message ||
                        'Processed successfully',
                })
                hideDialog()
            } else {
                setErrMsg(response.data?.errors.join('<br />'))
            }
        } catch (err: any) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any, config as any)
        }
    }

    const hideDialog = () => {
        setPageDialog(false)
        if (onClose) {
            onClose()
        } else {
            navigate('/content/pages')
        }
    }

    const formFooter = (
        <React.Fragment>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={hideDialog}
            >
                <span className="small">Cancel</span>
            </Button>
            {isViewOrEdit && (
                <DeleteDialog
                    url={'/page-components/pages/delete/' + id}
                    record={page}
                    deletedCallback={() => hideDialog()}
                    btnLabel="Delete"
                    showIcon={false}
                    confirmText={page.code}
                    message={
                        <>
                            <p className="mb-3 pb-3 border-bottom">
                                Are you sure you want to delete page:
                                <strong> {page.name}</strong>?
                            </p>
                            <Alert variant="warning" className="border-0">
                                <strong>Warning!</strong> All sections related
                                to this page will be deleted permenantly!
                            </Alert>
                        </>
                    }
                />
            )}
            <Button
                variant="success"
                disabled={isSubmitting || isLoading}
                // onClick={saveRecord}
                onClick={() => {
                    const formSubmitGtn = document.getElementById(
                        'page-form-submit-btn'
                    )
                    formSubmitGtn?.click()
                }}
            >
                <span className="small">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            {config?.saveButton?.savingLabel || 'Processing...'}
                        </>
                    ) : (
                        config?.saveButton?.label || 'Process'
                    )}
                </span>
            </Button>
        </React.Fragment>
    )

    const FormContent = (
        <>
            {isLoading && <Common.Loader text="Loading..." />}
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <Formik
                initialValues={initialValues}
                validationSchema={PageFormSchema}
                onSubmit={(values) => {
                    saveRecord(values)
                }}
                enableReinitialize={true}
            >
                {(formikProps: any) => {
                    return (
                        <FormikForm id="page-form">
                            <RenderFormikErrors formikProps={formikProps} />
                            <Row>
                                <Col lg={4}>
                                    <fieldset>
                                        <legend>General</legend>
                                        <Row>
                                            <Col xl={12}>
                                                <TextField
                                                    id="formPageTitle"
                                                    name="page_title"
                                                    label="Page Title"
                                                    placeholder="Enter page title"
                                                    formikProps={formikProps}
                                                    onBlur={(e) => {
                                                        setPage((prevState) => {
                                                            return {
                                                                ...prevState,
                                                                page_title:
                                                                    e.target
                                                                        .value,
                                                                code: slugify(
                                                                    formikProps
                                                                        .values
                                                                        .code ||
                                                                        formikProps
                                                                            .values
                                                                            .page_title
                                                                ),
                                                            }
                                                        })
                                                    }}
                                                />
                                                <TextField
                                                    id="formPageUrl"
                                                    name="code"
                                                    label="Page Url"
                                                    placeholder="Enter page name"
                                                    formikProps={formikProps}
                                                    readonly={
                                                        formikProps.values
                                                            .is_template
                                                    }
                                                    onBlur={(e) => {
                                                        formikProps.setFieldValue(
                                                            'code',
                                                            e.target.value
                                                                ?.toString()
                                                                .toLowerCase()
                                                        )
                                                    }}
                                                    required
                                                />
                                                <TextField
                                                    id="formPageInternalName"
                                                    name="name"
                                                    label="Page Internal Name"
                                                    placeholder="Enter page internal name"
                                                    formikProps={formikProps}
                                                    required
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xl={6}></Col>
                                            <Col xl={6}></Col>
                                        </Row>
                                    </fieldset>

                                    <fieldset>
                                        <legend>Meta Data</legend>
                                        <TextAreaField
                                            id="formMetaDataDescription"
                                            name="meta_description"
                                            label="Description"
                                            formikProps={formikProps}
                                        />
                                        <TextAreaField
                                            id="formMetaDataKeywords"
                                            name="meta_keywords"
                                            label="Keywords"
                                            formikProps={formikProps}
                                        />
                                    </fieldset>
                                </Col>
                                <Col lg={8}>
                                    <fieldset>
                                        <legend>
                                            Headers &amp; Background Image
                                        </legend>
                                        <Row>
                                            <Col xl={6}>
                                                <RenderTranslatedFields
                                                    id="pageHeadersTranslatedField"
                                                    formikProps={formikProps}
                                                    fields={[
                                                        {
                                                            id: 'pageHeadersMainHeaderTop',
                                                            type: 'text',
                                                            name: 'main_header_top',
                                                            label: 'Main Header Top',
                                                            placeholder:
                                                                'Enter main header top',
                                                        },
                                                        {
                                                            id: 'pageHeadersMainHeaderMiddleBig',
                                                            type: 'text',
                                                            name: 'main_header_middle_big',
                                                            label: 'Main Header Middle (Big)',
                                                            placeholder:
                                                                'Enter main header middle (big)',
                                                        },
                                                        {
                                                            id: 'pageHeadersMainHeaderBottom',
                                                            type: 'text',
                                                            name: 'main_header_bottom',
                                                            label: 'Main Header Bottom',
                                                            placeholder:
                                                                'Enter main header bottom',
                                                        },
                                                    ]}
                                                />
                                                <SelectField
                                                    id="pageHeadersHeaderSize"
                                                    name="header_size_id"
                                                    label="Header Size"
                                                    formikProps={formikProps}
                                                    choices={headerSizes}
                                                />
                                            </Col>
                                            <Col xl={6}>
                                                <ImageField
                                                    id="pageImageFile"
                                                    name="header_image"
                                                    label="Image"
                                                    formikProps={formikProps}
                                                    imageId={
                                                        page.header_image_id
                                                    }
                                                    alt={`${page.name} image`}
                                                    allowRemoveImage={
                                                        allowRemoveImage
                                                    }
                                                    setAllowRemoveImage={
                                                        setAllowRemoveImage
                                                    }
                                                    previewSrc={previewSrc}
                                                    setPreviewSrc={
                                                        setPreviewSrc
                                                    }
                                                    updateRecord={setPage}
                                                />
                                            </Col>
                                        </Row>
                                    </fieldset>
                                    <fieldset>
                                        <legend>Header Actions</legend>
                                        <CallToActions
                                            formikProps={formikProps}
                                            translations={translations}
                                            availablePages={availablePages}
                                            deletedCtas={deletedCtas}
                                            setDeletedCtas={setDeletedCtas}
                                        />
                                    </fieldset>
                                </Col>
                            </Row>
                            <input
                                type="submit"
                                id="page-form-submit-btn"
                                className="d-none"
                            />
                        </FormikForm>
                    )
                }}
            </Formik>
        </>
    )

    return (
        <>
            {inPopup ? (
                <Card className="flex justify-content-center">
                    <Dialog
                        visible={pageDialog}
                        breakpoints={{ '960px': '100vw', '640px': '100vw' }}
                        style={{ width: '75vw' }}
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : page.name
                                : 'Create new page'
                        }
                        modal
                        className="p-fluid"
                        footer={formFooter}
                        onHide={hideDialog}
                        maximizable
                        contentClassName={isLoading ? 'wrapped-content' : ''}
                        closeOnEscape={false}
                    >
                        {FormContent}
                    </Dialog>
                </Card>
            ) : (
                <>
                    <PageHeader
                        header={
                            isViewOrEdit
                                ? isLoading
                                    ? 'Loading...'
                                    : page.name
                                : 'Create new page'
                        }
                        buttons={[
                            {
                                variant: theme === 'dark' ? 'dark' : 'light',
                                label: 'Back to Pages',
                                icon: 'fa-solid fa-arrow-left',
                                url: '/content/pages',
                            },
                        ]}
                    />
                    {FormContent}
                    <FormActions formFooter={formFooter} />
                </>
            )}
        </>
    )
}

export default PageForm
